import EditHeader from "common/components/EditHeader/EditHeader";
import HeaderButtonsEdit from "common/components/EditHeader/HeaderButtonsEdit";
import FormControl from "common/components/FormControl/FormControl";
import FormScrollError from "common/components/FormScrollError/FormScrollError";
import useRouter from "common/hooks/use-router";
import { useMemo, useState } from "react";
import { Form } from "react-final-form";
import { useTranslation } from "react-i18next";
import arrayMutators from "final-form-arrays";
import { Content } from "types/Content";
import CategorySelect from "components/select/CategorySelect/CategorySelect";
import ProfileSelect from "components/select/ProfileSelect/ProfileSelect";
import ContentStatusSelect from "components/select/ContentStatusSelect/ContentStatusSelect";
import SectionTitle from "common/components/SectionTitle/SectionTitle";
import AddressSelect from 'components/select/AddressSelect/AddressSelect';

type ContentFormProps = {
  content: Content | undefined;
  onSubmit: (values: any) => any;
};

function ContentForm({ onSubmit, content }: ContentFormProps) {
  const { t } = useTranslation();
  const [errorMessage, setErrorMessage] = useState('');
  const { navigate } = useRouter();

  const initialValues = useMemo(
    () =>
      content?.id
        ? {
            ...content,
            category: content.category?.id,
            author: content.author?.id,
            address: content.address?.id,
          }
        : {
            name: '',
            label: '',
            status: 'draft',
          },
    [content]
  );

  const onSubmitWrapper = async (values: any) => {
    const res = await onSubmit(values);

    if (res.message) {
      setErrorMessage(res.message);
    }
  };

  return (
    <Form
      onSubmit={onSubmitWrapper}
      subscription={{ values: true, submitting: true, pristine: true }}
      initialValues={initialValues}
      mutators={{
        ...arrayMutators,
        setFieldValue: ([f]: any, state: any, utils: any) => {
          utils.changeValue(state, f.field, () => f.value);
        },
      }}
      render={({ handleSubmit, values }) => {
        return (
          <div className="mx-auto max-w-full py-4 px-4 text-slate-500 text-base">
            {/* Header */}
            <EditHeader
              title={t(content?.id ? 'editItem.content' : 'newItem.content')}
              subTitle={values.name}
              icon="HandThumbUpIconSolid"
              errorMessage={errorMessage}
              HeaderButtonsEdit={
                <HeaderButtonsEdit
                  inHeader
                  onSubmit={handleSubmit}
                  onCancel={() => navigate('/contents')}
                />
              }
            />
            <form onSubmit={handleSubmit} noValidate>
              <FormScrollError />

              <div className="responsive-flex gap-2 mt-2">
                <div className="md:w-2/3 flex flex-col gap-2">
                  <div className="white-box flex flex-col w-full gap-2">
                    <FormControl name="title" title={t('common.title')} required />
                    <FormControl
                      className="w-full"
                      name="description"
                      type="textarea"
                      titleKey="forms.description"
                      required
                    />
                    <div className="w-full flex flex-row gap-4">
                      <FormControl
                        name="price"
                        type="price"
                        titleKey="forms.price"
                        className="w-full flex-1 rounded-md"
                        containerStyle="flex-1"
                        disabled={values.noPrice}
                        value={values.noPrice ? null : values.price}
                      />
                      <FormControl
                        name="noPrice"
                        titleKey="forms.noPrice"
                        type="switch"
                        containerStyle="flex-1 flex flex-row-reverse gap-2 justify-end items-end mb-2"
                      />
                    </div>
                    <FormControl name="address" titleKey="forms.address" type="custom">
                      <AddressSelect />
                    </FormControl>
                    <FormControl
                      title={t('forms.images')}
                      labelIcon="PhotoIcon"
                      name="images"
                      type="images"
                      limit={4}
                    />
                  </div>
                  <div className="white-box flex flex-col w-full gap-2">
                    <SectionTitle title={t('forms.visibility')} icon="EyeIcon" />
                    <FormControl name="category" titleKey="forms.category" type="custom">
                      <CategorySelect />
                    </FormControl>
                    <FormControl
                      name="onlyForPro"
                      titleKey="forms.onlyForPro"
                      type="switch"
                      containerStyle="flex-1 flex flex-row-reverse gap-4 justify-end items-end my-2"
                    />
                  </div>
                </div>

                <div className="flex flex-col md:w-1/3 gap-2">
                  <div className="white-box flex flex-col w-full gap-2">
                    <SectionTitle title={t('forms.associatedProfile')} icon="UserIcon" />
                    <FormControl name="authorProfile" titleKey="forms.author" type="custom">
                      <ProfileSelect />
                    </FormControl>
                  </div>
                  <div className="white-box flex flex-col w-full gap-2">
                    <SectionTitle title={t('forms.state')} icon="Status" />
                    <FormControl
                      name="status"
                      titleKey="common.status"
                      type="custom"
                      disabled={!content?.id}
                    >
                      <ContentStatusSelect />
                    </FormControl>

                    {content?.id && (
                      <>
                        <FormControl
                          name="publishedDate"
                          titleKey="forms.publishedDate"
                          type="date"
                        />
                        <FormControl
                          name="endPublishedDate"
                          titleKey="forms.endPublishedDate"
                          type="date"
                        />
                      </>
                    )}
                  </div>
                </div>
              </div>
              <HeaderButtonsEdit onSubmit={handleSubmit} onCancel={() => navigate('/contents')} />
            </form>
          </div>
        );
      }}
    />
  );
}

export default ContentForm;
